import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  securityGroupFormSchema,
  securityGroupEditFormSchema,
  type SecurityGroupFormInput,
  type SecurityGroupFormOutput,
  type SecurityGroupEditFormInput,
  type SecurityGroupEditFormOutput,
} from '../schemas/securityGroup.schemas';
import { Form, Input, Button, Label, Select } from '@/shared/components';
import { useDealersQuery } from '@/features/dealers/hooks/useDealersQuery';
import type { InvoicingMode } from '../types/security-admin.types';
import { InvoicingModeField } from './InvoicingModeField';

// ---------------------------------------------------------------------------
// Create-form props (includes owner_dealer_id selection)
// ---------------------------------------------------------------------------
interface SecurityGroupCreateFormProps {
  mode: 'create';
  initialValues?: { name?: string; owner_dealer_id?: string; invoicing_mode?: InvoicingMode };
  onSubmit: (data: SecurityGroupFormOutput) => void;
  isLoading?: boolean;
  submitLabel?: string;
}

// ---------------------------------------------------------------------------
// Edit-form props (owner_dealer_id is immutable post-create)
// ---------------------------------------------------------------------------
interface SecurityGroupEditFormProps {
  mode: 'edit';
  initialValues?: { name: string; invoicing_mode: InvoicingMode };
  productsCount: number;
  onSubmit: (data: SecurityGroupEditFormOutput) => void;
  isLoading?: boolean;
  submitLabel?: string;
}

type SecurityGroupFormProps = SecurityGroupCreateFormProps | SecurityGroupEditFormProps;

// ---------------------------------------------------------------------------
// Shared inner form
// ---------------------------------------------------------------------------
export function SecurityGroupForm(props: SecurityGroupFormProps) {
  return props.mode === 'create'
    ? <CreateForm {...props} />
    : <EditForm {...props} />;
}

// ---------------------------------------------------------------------------
// Create form — renders name, owner_dealer_id and invoicing mode
// ---------------------------------------------------------------------------
function CreateForm({
  initialValues,
  onSubmit,
  isLoading = false,
  submitLabel = 'Save',
}: SecurityGroupCreateFormProps) {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<SecurityGroupFormInput, unknown, SecurityGroupFormOutput>({
    resolver: zodResolver(securityGroupFormSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      owner_dealer_id: initialValues?.owner_dealer_id ?? '',
      invoicing_mode: initialValues?.invoicing_mode ?? 'relaxed_open',
    },
  });

  const dealersQuery = useDealersQuery({ per_page: 50 });
  const dealerOptions = (dealersQuery.data?.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));

  return (
    <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="name">Name</Label>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="name"
              placeholder="Security group name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!error}
              hint={error?.message}
            />
          )}
        />
      </div>

      <div>
        <Label htmlFor="owner_dealer_id">Parent Dealer</Label>
        <Controller
          name="owner_dealer_id"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              id="owner_dealer_id"
              options={dealerOptions}
              placeholder="Select parent dealer"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!error}
              hint={error?.message}
              isLoading={dealersQuery.isLoading}
            />
          )}
        />
      </div>


      <Controller
        name="invoicing_mode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InvoicingModeField
            id="invoicing_mode"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            productsCount={0}
            error={error?.message}
          />
        )}
      />

      <Button size="sm" disabled={isLoading || isSubmitting}>
        {isLoading || isSubmitting ? 'Saving...' : submitLabel}
      </Button>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Edit form — name and invoicing mode; owner_dealer_id is read-only after creation
// ---------------------------------------------------------------------------
function EditForm({
  initialValues,
  productsCount,
  onSubmit,
  isLoading = false,
  submitLabel = 'Save',
}: SecurityGroupEditFormProps) {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<SecurityGroupEditFormInput, unknown, SecurityGroupEditFormOutput>({
    resolver: zodResolver(securityGroupEditFormSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      invoicing_mode: initialValues?.invoicing_mode ?? 'relaxed_open',
    },
  });

  return (
    <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Label htmlFor="name">Name</Label>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="name"
              placeholder="Security group name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={!!error}
              hint={error?.message}
            />
          )}
        />
      </div>


      <Controller
        name="invoicing_mode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InvoicingModeField
            id="invoicing_mode"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            productsCount={productsCount}
            error={error?.message}
          />
        )}
      />

      <Button size="sm" disabled={isLoading || isSubmitting}>
        {isLoading || isSubmitting ? 'Saving...' : submitLabel}
      </Button>
    </Form>
  );
}
